import { createContext, useState, useCallback, useRef } from "react";
import api from "../api/client";

export const OwnerAuthContext = createContext({
  owner: null,
  ownerLoading: false,
  ownerSessionChecked: false,
  refreshOwnerAuth: async () => {},
  setOwner: () => {},
  ownerLogout: async () => {},
});

const LS_KEYS_TO_CLEAR = [
  "pendingDeviceToken",
  "lastSentDeviceToken_user",
  "lastSentDeviceToken_owner",
  "loginType",
];

export const OwnerAuthProvider = ({ children }) => {
  const [owner, setOwnerState] = useState(null);
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [ownerSessionChecked, setOwnerSessionChecked] = useState(false);
  const seq = useRef(0);

  const setOwner = useCallback((value) => {
    seq.current += 1;
    setOwnerState(value);
    setOwnerLoading(false);
    setOwnerSessionChecked(true);
  }, []);

  const refreshOwnerAuth = useCallback(() => {
    const id = ++seq.current;
    setOwnerLoading(true);
    return (async () => {
      try {
        const res = await api.get("/api/auth/owner/me", {
          timeout: 20_000,
          skipSessionExpired: true,
        });
        if (id !== seq.current) return;
        setOwnerState(res.status === 200 && res.data?.ownerId ? res.data : null);
      } catch {
        if (id !== seq.current) return;
        setOwnerState(null);
      } finally {
        if (id === seq.current) {
          setOwnerLoading(false);
          setOwnerSessionChecked(true);
        }
      }
    })();
  }, []);

  const ownerLogout = async () => {
    try {
      const fcm = await import("../app/FCMTokenHandler");
      await fcm.removeDeviceTokenOnLogout("owner");
      await api.post("/api/google/logout");
    } catch {
      // ignore
    } finally {
      setOwner(null);
      LS_KEYS_TO_CLEAR.forEach((k) => localStorage.removeItem(k));
    }
  };

  return (
    <OwnerAuthContext.Provider
      value={{ owner, ownerLoading, ownerSessionChecked, refreshOwnerAuth, setOwner, ownerLogout }}
    >
      {children}
    </OwnerAuthContext.Provider>
  );
};
